/**
 * FlaggedPanel — reusable dedicated view for a slice of transactions
 * (Personal, Unknown, Loans, …). Renders a header, three summary tiles
 * and an inline-editable table. Pass `filter`, `title`, `icon` and
 * `emptyText` from the caller.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Trash2, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import {
  listTransactions, updateTransaction, deleteTransaction, money,
} from "@/lib/operations/api";

export default function FlaggedPanel({
  config,
  refreshKey,
  onChange,
  sponsor,               // global sponsor filter (may be "")
  filter,                // params passed straight to listTransactions
  title,
  subtitle,
  emptyIcon: EmptyIcon = TrendingDown,
  emptyTitle,
  emptyBody,
  icon: HeaderIcon,
  iconBgClass = "bg-brand-navy",
  totalLabel = "Total",
  testidPrefix,          // e.g. "ops-personal", "ops-unknown", "ops-loans"
  loadErrorText,
  showCostCentre = true,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Combine caller-provided filter with the global sponsor. Sponsor
  // becomes a `company` query param; omitted when "All".
  const finalFilter = { ...(filter || {}), ...(sponsor ? { company: sponsor } : {}) };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listTransactions(finalFilter);
      setRows(list);
    } catch {
      toast.error(loadErrorText || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(finalFilter)]);

  useEffect(() => { reload(); }, [reload, refreshKey]);

  const total = useMemo(
    () => rows.reduce((s, r) => s + Number(r.amount || 0), 0),
    [rows],
  );
  const thisMonthKey = new Date().toISOString().slice(0, 7);
  const totalThisMonth = useMemo(
    () => rows
      .filter((r) => (r.date || "").slice(0, 7) === thisMonthKey)
      .reduce((s, r) => s + Number(r.amount || 0), 0),
    [rows, thisMonthKey],
  );

  const patch = async (id, body) => {
    try {
      await updateTransaction(id, body);
      setRows((r) => r.map((t) => (t.id === id ? { ...t, ...body } : t)));
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const removeRow = async (id) => {
    if (!window.confirm("Delete this transaction? This cannot be undone.")) return;
    try {
      await deleteTransaction(id);
      setRows((r) => r.filter((t) => t.id !== id));
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid={`${testidPrefix}-panel`}>
      {/* Header + summary tiles */}
      <div className="flex items-center gap-3">
        {HeaderIcon && (
          <div className={`h-10 w-10 rounded-xl ${iconBgClass} text-white flex items-center justify-center shrink-0`}>
            <HeaderIcon className="h-5 w-5" />
          </div>
        )}
        <div>
          <h2 className="text-xl font-bold font-heading text-brand-navy leading-tight">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xs text-slate-500">{subtitle}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryTile
          label={totalLabel}
          value={money(total)}
          sub={`${rows.length} transaction${rows.length === 1 ? "" : "s"}`}
          testid={`${testidPrefix}-summary-total`}
        />
        <SummaryTile
          label="This month"
          value={money(totalThisMonth)}
          sub={new Date().toLocaleString("en-US", { month: "long", year: "numeric" })}
          testid={`${testidPrefix}-summary-month`}
        />
        <SummaryTile
          label="Avg per transaction"
          value={rows.length ? money(total / rows.length) : "$0.00"}
          sub="Signed magnitude"
          testid={`${testidPrefix}-summary-avg`}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white/60 p-10 text-center">
          <EmptyIcon className="h-6 w-6 text-slate-300 mx-auto" />
          <p className="mt-3 text-brand-navy font-bold text-lg">{emptyTitle}</p>
          {emptyBody && (
            <p className="mt-1 text-sm text-slate-500">{emptyBody}</p>
          )}
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid={`${testidPrefix}-table`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 text-slate-500 sticky top-0 z-10">
                <tr className="whitespace-nowrap">
                  <th className="text-left font-semibold px-3 py-2 text-[10px] uppercase tracking-wider w-28">Date</th>
                  <th className="text-left font-semibold px-3 py-2 text-[10px] uppercase tracking-wider">Description</th>
                  <th className="text-right font-semibold px-3 py-2 text-[10px] uppercase tracking-wider w-28">Amount</th>
                  <th className="text-left font-semibold px-3 py-2 text-[10px] uppercase tracking-wider w-32">Company</th>
                  {showCostCentre && (
                    <th className="text-left font-semibold px-3 py-2 text-[10px] uppercase tracking-wider w-36">Cost centre</th>
                  )}
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((t) => (
                  <tr key={t.id} className="whitespace-nowrap" data-testid={`${testidPrefix}-row-${t.id}`}>
                    <td className="px-3 py-2 tabular-nums text-slate-600">{t.date}</td>
                    <td className="px-3 py-2 text-brand-navy font-medium max-w-[320px] truncate" title={t.description}>
                      <span className="inline-flex items-center gap-1.5">
                        {t.source === "manual" && (
                          <span
                            className="inline-flex items-center px-1.5 h-4 rounded bg-brand-teal/10 text-brand-teal text-[9px] font-bold tracking-wide shrink-0"
                            title="Manually entered (not from a statement)"
                          >
                            M
                          </span>
                        )}
                        <span className="truncate">{t.description}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-brand-navy">
                      {money(t.amount)}
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-xs">
                      {t.company || <span className="text-slate-300">—</span>}
                    </td>
                    {showCostCentre && (
                      <td className="px-3 py-2">
                        <select
                          value={t.cost_centre || ""}
                          onChange={(e) => patch(t.id, { cost_centre: e.target.value })}
                          className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                          data-testid={`${testidPrefix}-cc-${t.id}`}
                        >
                          <option value="">—</option>
                          {config.cost_centres.map((c) => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                          ))}
                        </select>
                      </td>
                    )}
                    <td className="px-2 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => removeRow(t.id)}
                        className="h-7 w-7 rounded-md text-red-500 hover:bg-red-50 flex items-center justify-center"
                        title="Delete"
                        data-testid={`${testidPrefix}-delete-${t.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, sub, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className="mt-2 text-3xl font-bold font-heading tabular-nums text-brand-navy">{value}</p>
      <p className="mt-1 text-xs font-medium text-slate-500">{sub}</p>
    </div>
  );
}
